import React, { useState, useEffect, useRef } from "react";
import { useAuthContext } from "../lib/authLib";
import * as charitiesLib from "../lib/charitiesLib";
import { FaPlus, FaEdit } from "react-icons/fa";

interface CharityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (charity: Omit<charitiesLib.Charity, "id">) => Promise<void>;
  initialData?: charitiesLib.Charity;
}

const CharityModal: React.FC<CharityModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData,
}) => {
  const [charity, setCharity] = useState<Omit<charitiesLib.Charity, "id">>(
    initialData
      ? {
          name: initialData.name,
          url: initialData.url || "",
          blurb: initialData.blurb || "",
          logoUrl: initialData.logoUrl || "",
          heroUrl: initialData.heroUrl || "",
          status: initialData.status || charitiesLib.CharityStatus.ACTIVE,
        }
      : {
          name: "",
          url: "",
          blurb: "",
          logoUrl: "",
          heroUrl: "",
          status: charitiesLib.CharityStatus.ACTIVE,
        }
  );

  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (initialData) {
      setCharity({
        name: initialData.name,
        url: initialData.url || "",
        blurb: initialData.blurb || "",
        logoUrl: initialData.logoUrl || "",
        heroUrl: initialData.heroUrl || "",
        status: initialData.status || charitiesLib.CharityStatus.ACTIVE,
      });
    }
  }, [initialData]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setCharity((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave(charity);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        ref={modalRef}
        className="bg-white dark:bg-gray-800 p-6 rounded-lg w-full max-w-md"
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">
          {initialData ? "Edit Charity" : "Add New Charity"}
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 dark:text-gray-300 mb-2">
              Name *
            </label>
            <input
              type="text"
              name="name"
              value={charity.name}
              onChange={handleChange}
              className="w-full p-2 border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 dark:text-gray-300 mb-2">
              Website URL
            </label>
            <input
              type="url"
              name="url"
              value={charity.url || ""}
              onChange={handleChange}
              className="w-full p-2 border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 dark:text-gray-300 mb-2">
              Description
            </label>
            <textarea
              name="blurb"
              value={charity.blurb || ""}
              onChange={handleChange}
              className="w-full p-2 border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600"
              rows={3}
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 dark:text-gray-300 mb-2">
              Logo URL
            </label>
            <input
              type="url"
              name="logoUrl"
              value={charity.logoUrl || ""}
              onChange={handleChange}
              className="w-full p-2 border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 dark:text-gray-300 mb-2">
              Hero Image URL
            </label>
            <input
              type="url"
              name="heroUrl"
              value={charity.heroUrl || ""}
              onChange={handleChange}
              className="w-full p-2 border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 dark:text-gray-300 mb-2">
              Status
            </label>
            <select
              name="status"
              value={charity.status as string}
              onChange={handleChange}
              className="w-full p-2 border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600"
            >
              <option value={charitiesLib.CharityStatus.ACTIVE}>Active</option>
              <option value={charitiesLib.CharityStatus.DISABLED}>
                Disabled
              </option>
              <option value={charitiesLib.CharityStatus.HIDDEN}>Hidden</option>
            </select>
          </div>
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 dark:bg-gray-700 rounded hover:bg-gray-400 dark:hover:bg-gray-600 dark:text-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              {initialData ? "Update" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface CharityCardProps {
  charity: charitiesLib.Charity;
  isAdmin: boolean;
  onEdit: (charity: charitiesLib.Charity) => void;
}

const CharityCard: React.FC<CharityCardProps> = ({
  charity,
  isAdmin,
  onEdit,
}) => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
      <div className="h-40 bg-gray-200 dark:bg-gray-700 relative">
        {charity.heroUrl ? (
          <img
            src={charity.heroUrl}
            alt={`${charity.name} banner`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <span className="text-gray-400 dark:text-gray-500">
              No hero image
            </span>
          </div>
        )}
        {charity.logoUrl && (
          <div className="absolute bottom-2 left-2 h-16 w-16 rounded-full bg-white dark:bg-gray-800 p-1 shadow">
            <img
              src={charity.logoUrl}
              alt={`${charity.name} logo`}
              className="w-full h-full object-contain rounded-full"
            />
          </div>
        )}
        {isAdmin && (
          <button
            onClick={() => onEdit(charity)}
            className="absolute top-2 right-2 p-2 bg-white dark:bg-gray-800 rounded-full shadow hover:bg-gray-100 dark:hover:bg-gray-700"
            title="Edit charity"
          >
            <FaEdit className="text-gray-600 dark:text-gray-300" />
          </button>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-bold text-lg mb-1 dark:text-white">
          {charity.name}
        </h3>
        {charity.blurb && (
          <p className="text-gray-700 dark:text-gray-300 text-sm line-clamp-2">
            {charity.blurb}
          </p>
        )}
        {charity.url && (
          <a
            href={charity.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 hover:text-blue-700 text-sm mt-2 inline-block"
          >
            Visit website
          </a>
        )}
        {charity.status !== charitiesLib.CharityStatus.ACTIVE && (
          <div className="mt-2">
            <span className="text-xs px-2 py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
              {charity.status === charitiesLib.CharityStatus.DISABLED
                ? "Disabled"
                : "Hidden"}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

const Charities: React.FC = () => {
  const [charities, setCharities] = useState<charitiesLib.Charity[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCharity, setEditingCharity] =
    useState<charitiesLib.Charity | null>(null);
  const { user } = useAuthContext();

  const fetchCharities = async () => {
    try {
      setLoading(true);
      const data = await charitiesLib.getAll();
      setCharities(data);
    } catch (error) {
      console.error("Failed to fetch charities:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCharities();
  }, []);

  const handleSaveCharity = async (
    charityData: Omit<charitiesLib.Charity, "id">
  ) => {
    try {
      if (editingCharity) {
        await charitiesLib.update(editingCharity.id, charityData);
      } else {
        await charitiesLib.create(charityData);
      }
      fetchCharities();
    } catch (error) {
      console.error("Failed to save charity:", error);
    }
  };

  const handleEditCharity = (charity: charitiesLib.Charity) => {
    setEditingCharity(charity);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCharity(null);
  };

  const isAdmin = user?.isSuperAdmin ?? false;

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold dark:text-white">Charities</h1>
        {isAdmin && (
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
          >
            <FaPlus /> Add Charity
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : charities.length === 0 ? (
        <div className="text-center py-12 dark:text-white">
          <p>No charities found.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
          {charities.map((charity) => (
            <CharityCard
              key={charity.id}
              charity={charity}
              isAdmin={isAdmin}
              onEdit={handleEditCharity}
            />
          ))}
        </div>
      )}

      <CharityModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveCharity}
        initialData={editingCharity || undefined}
      />
    </div>
  );
};

export default Charities;
